import { useEffect, useState, useRef } from "react";
import { ContainerWithSidebar } from "../../../components/layout/index";
import { SearchInput } from "../../../components/input";
import { MenuButton, Button, IconButton } from "../../../components/button";
import Spinner from "../../../components/spinner";
import axios from "axios";
import { FiPlus } from "react-icons/fi";
import { BiArrowToTop } from "react-icons/bi";
import { IoMdCart } from "react-icons/io";
import { AiOutlineCloseCircle, AiOutlineSearch } from "react-icons/ai";
import { BsTrash } from "react-icons/bs";
import { Modal } from "@material-ui/core";
import _ from "lodash";
import ParentCategory from "./ParentCategory";
import Category from "./Category";
import Item from "./Item";
import { useTranslation } from 'react-i18next';
import useDebounce from "hooks/useDebounce";

import styles from "./index.module.css";
import CheckedBoxButton from "components/checkBoxButton";

function parseCategoryId(value) {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Items with no category and no root must still appear under Uncategorized. */
function itemMatchesPurchasingCategory(item, category, parentRootId) {
  const itemCategoryId = parseCategoryId(item.category_id);
  const itemRootId = parseCategoryId(item.root_id);
  const parentId = parseCategoryId(parentRootId);

  if (category.categoryId != null) {
    return itemCategoryId === category.categoryId;
  }

  if (itemCategoryId != null) {
    return false;
  }

  if (parentId == null) {
    return itemRootId == null;
  }

  return itemRootId === parentId;
}

const Order = (props) => {
  const { t } = useTranslation();
  const tBase = "views.purchasing.order.index";
  const tr = (key) => t(`${tBase}.${key}`);

  const pickerRef = useRef(null);
  const [filter, setFilter] = useState("All Items");
  const [isLoading, setIsLoading] = useState(true);
  const [currentCart, setCurrentCart] = useState([]);
  const [currentCartIds, setCurrentCartIds] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  // ESERV2-1431: one /purchasingItems round trip per typing pause, not per
  // keystroke — the fetch reads the live searchParam, so the request that
  // fires carries the current text.
  const debouncedSearchParam = useDebounce(searchParam, 300);
  const [parentCategories, setParentCategories] = useState(null);
  const [categories, setCategories] = useState(null);
  const [addRecommendedToCart, setAddRecommendedToCart] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [preferredVendorId, setPreferredVendorId] = useState(null);

  const addToCart = async (itemId, quantity) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v2/eserv-business/purchasing/addToPurchasingCart`,
        {
          itemId: parseInt(itemId, 10),
          quantity: parseFloat(quantity),
        }
      );

      // Use cart data directly from response
      if (response.data && response.data.currentCart) {
        setCurrentCart(response.data.currentCart);
        const idList = response.data.currentCart.map((item) => item.item_id);
        setCurrentCartIds(idList);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  };

  const fetchItems = async () => {
    try {
      const params = { searchParam: searchParam || undefined };
      if (preferredVendorId != null) {
        params.vendorId = preferredVendorId;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v2/eserv-business/purchasing/purchasingItems`,
        { params }
      );

      function roundToTwoDecimals(value) {
        return Math.round(value * 100) / 100;
      }

      const data = response.data.map((item) => {
        item.in_stock = roundToTwoDecimals(item.in_stock);
        item.minimum = roundToTwoDecimals(item.minimum);
        item.maximum = roundToTwoDecimals(item.maximum);
        item.outstanding = roundToTwoDecimals(item.outstanding);

        return item;
      });

      setFilteredItems(data);
    } catch (error) {
      console.error("Error fetching purchasing items:", error);
    } finally {
      setIsLoading(false);
    }
  };



  const fetchVendors = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/fetchVendors`
      );
      setVendors(response.data || []);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  const fetchCurrentCarts = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/getCarts`
      );

      setCurrentCart(response.data.currentCart || []);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  };

  const bulkAddToCart = async (items) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v2/eserv-business/purchasing/bulkAddToCart`,
        {
          items: items.map((item) => ({
            itemId: parseInt(item.itemId, 10),
            quantity: parseFloat(item.quantity),
          })),
        }
      );

      // Use cart data directly from response
      if (response.data && response.data.currentCart) {
        setCurrentCart(response.data.currentCart);
        const idList = response.data.currentCart.map((item) => item.item_id);
        setCurrentCartIds(idList);
      }

      return response.data;
    } catch (error) {
      console.error('Error bulk adding to cart:', error);
      throw error;
    }
  };

  const bulkRemoveCartItems = async (cartItemIds) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_SERVER_URL}/api/v2/eserv-business/purchasing/bulkRemoveCartItems`,
        {
          data: {
            cartItemIds: cartItemIds,
          },
        }
      );

      // Use cart data directly from response
      if (response.data && response.data.currentCart) {
        setCurrentCart(response.data.currentCart);
        const idList = response.data.currentCart.map((item) => item.item_id);
        setCurrentCartIds(idList);
      }

      return response.data;
    } catch (error) {
      console.error('Error bulk removing from cart:', error);
      throw error;
    }
  };

  const handleScrollTo = (id) => {
    const element = document.getElementById(`pc-${id}`);

    const offset = 60;
    const bodyRect = document.body.getBoundingClientRect().top;
    const elementRect = element.getBoundingClientRect().top;
    const elementPosition = elementRect - bodyRect;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };

  const removeCartItem = async (id) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_SERVER_URL}/api/v2/eserv-business/purchasing/removeCartItem`,
        {
          data: {
            cartItemId: id,
          },
        }
      );

      // Use cart data directly from response
      if (response.data && response.data.currentCart) {
        setCurrentCart(response.data.currentCart);
        const idList = response.data.currentCart.map((item) => item.item_id);
        setCurrentCartIds(idList);
      }

      // Return the updated cart so Cart component can use it directly
      return response.data?.currentCart || null;
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    }
  };

  const handleClose = () => {
    fetchCurrentCarts();
    setModalOpen(false);
  };

  useEffect(() => {
    fetchCurrentCarts();
    fetchVendors();
  }, []);

  useEffect(() => {
    setIsLoading(true);
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchParam, preferredVendorId]);


  useEffect(() => {
    const roots = filteredItems.map((i) => {
      return {
        rootId: parseInt(i.root_id) || null,
        rootName: i.root_name || tr("Uncategorized"),
      };
    });
    const uniqueRoots = _.uniqBy(roots, "rootId");
    setParentCategories(uniqueRoots);

    const cats = filteredItems.map((i) => {
      const categoryId = parseInt(i.category_id) || null;
      const rootId = parseInt(i.root_id) || null;
      const parentCategoryId = categoryId
        ? parseInt(i.parent_category_id) || null
        : rootId;

      return {
        categoryId,
        categoryName: i.category_name || tr("Uncategorized"),
        parentCategoryId,
      };
    });
    const uniqueCats = _.uniqBy(
      cats,
      (c) => `${c.parentCategoryId ?? "none"}-${c.categoryId ?? "none"}`
    );
    setCategories(uniqueCats);
  }, [filteredItems]);

  useEffect(() => {
    if (currentCart.length > 0) {
      const idList = currentCart?.map((item) => {
        return item.item_id;
      });

      setCurrentCartIds(idList);
    } else {
      setCurrentCartIds([]);
    }
  }, [currentCart]);

  useEffect(() => {
    // for every item in filtered items, if the item has a recommended amount (maximum - (in_stock - outstanding)), add it to the cart
    if (addRecommendedToCart) {
      // Build array of items to add
      const itemsToAdd = filteredItems
        .filter((item) => {
          const recommendedAmount = (item.in_stock + (item.outstanding || 0)) < item.minimum
            ? item.maximum - (item.in_stock + (item.outstanding || 0))
            : 0;
          return recommendedAmount > 0 && !currentCartIds.includes(item.item_id);
        })
        .map((item) => {
          const recommendedAmount = (item.in_stock + (item.outstanding || 0)) < item.minimum
            ? item.maximum - (item.in_stock + (item.outstanding || 0))
            : 0;
          return {
            itemId: item.item_id,
            quantity: recommendedAmount,
          };
        });

      if (itemsToAdd.length > 0) {
        bulkAddToCart(itemsToAdd);
      }
    } else {
      // if addRecommendedToCart is false, remove all items from the cart
      if (currentCart.length > 0) {
        const allCartItemIds = currentCart.map((item) => item.cart_item_id);
        bulkRemoveCartItems(allCartItemIds);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addRecommendedToCart]);

  const handleShowEverything = () => {
    setPreferredVendorId(null);
    setSearchParam("");
    setFilter("All Items");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const selectedVendorName =
    preferredVendorId == null
      ? tr("All Vendors")
      : vendors.find((v) => String(v.vendor_id) === String(preferredVendorId))
          ?.vendor_name || tr("All Vendors");

  return (
    <>
      <div className="header-container">
        <div className="header-row">
          <div className="header">
            {preferredVendorId != null ? selectedVendorName : filter && filter === "All Items" ? tr("All Items") : filter}
          </div>
          <SearchInput
            className="search"
            placeholder={tr("Search")}
            value={searchParam}
            onChange={(e) => {
              setSearchParam(e.target.value);
            }}
          />
          <AiOutlineSearch
            style={{ marginLeft: 10, cursor: "pointer" }}
            size={20}
          />
          <div
            onClick={() => {
              setModalOpen(true);
            }}
            className="cart"
          >
            <IoMdCart size={18} />
            <div className="cart-count">{currentCart.length}</div>
          </div>
        </div>
        <CheckedBoxButton
          checkedValue={addRecommendedToCart}
          setCheckedValue={setAddRecommendedToCart}
          additionalStyling={{ marginRight: 0, marginLeft: "auto", marginTop: 10, marginBottom: 10, width: "auto" }}
        >
          {tr("Add All Recommended Amounts to Cart")}
        </CheckedBoxButton>
      </div>

      <Modal open={modalOpen} onClose={handleClose}>
        <Cart
          onCartSubmitted={props.onCartSubmitted}
          removeCartItem={removeCartItem}
          fetchCounts={props.fetchCounts}
          handleClose={handleClose}
        />
      </Modal>
      {/* The layout stays mounted through a refetch. This used to be
          `!isLoading ? <everything/> : <Spinner/>`, which unmounted the whole
          page — sidebar included — every time a vendor was selected, so the
          Vendors and Categories sections vanished and reloaded with the parts
          and the scroll position reset to the top. Only the parts panel
          actually depends on the fetch, so only it shows the spinner. */}
      <div className={styles.layoutRow}>
        <div className={styles.navigator}></div>
        <div className={styles.sidebarColumn}>
            <div className={styles.showEverythingWrap}>
              <Button
                primary
                onClick={handleShowEverything}
                className={styles.showEverythingButton}
              >
                {tr("Show Everything")}
              </Button>
            </div>

            <div className={styles.sidebarPanel}>
              <div className={styles.categoryHeader}>{tr("Vendors")}</div>
              <hr />
              <div className={styles.sidebarPanelContent}>
                <div
                  className={
                    preferredVendorId == null
                      ? styles.filterButtonSelected
                      : styles.filterButton
                  }
                  onClick={() => setPreferredVendorId(null)}
                >
                  {tr("All Vendors")}
                </div>
                {vendors &&
                  _.sortBy(vendors, ["vendor_name"])?.map((vendor) => (
                    <div
                      key={vendor.vendor_id}
                      className={
                        String(preferredVendorId) === String(vendor.vendor_id)
                          ? styles.filterButtonSelected
                          : styles.filterButton
                      }
                      onClick={() => setPreferredVendorId(vendor.vendor_id)}
                    >
                      {vendor.vendor_name}
                    </div>
                  ))}
              </div>
            </div>

            <div className={styles.sidebarPanel}>
              <div className={styles.categoryHeader}>{tr("Categories")}</div>
              <hr />
              <div className={styles.sidebarPanelContent}>
                {parentCategories &&
                  parentCategories.length > 0 &&
                  _.sortBy(parentCategories, ["rootId"])?.map((pc) => (
                    <div
                      key={pc.rootId}
                      className={styles.rootButton}
                      onClick={() => handleScrollTo(pc.rootId)}
                    >
                      {pc.rootName}
                    </div>
                  ))}
              </div>
            </div>
          </div>

          <div
            ref={pickerRef}
            className={styles.picker}
            style={{ paddingLeft: 20, paddingRight: 20 }}
          >
            {isLoading ? (
              <div className="spinner-container">
                <Spinner />
              </div>
            ) : (
              <>
            {parentCategories &&
              parentCategories.length > 0 &&
              _.sortBy(parentCategories, ["rootId"])?.map((pc) => {
                return (
                  <ParentCategory
                    key={pc.rootId}
                    rootId={pc.rootId}
                    rootName={pc.rootName}
                  >
                    {categories &&
                      categories.length > 0 &&
                      _.sortBy(
                        categories.filter(
                          (c) => pc.rootId === c.parentCategoryId
                        ),
                        ["categoryId"]
                      )?.map((c) => (
                        <Category
                          key={`${c.parentCategoryId ?? "none"}-${c.categoryId ?? "none"}`}
                          categoryName={c.categoryName}
                        >
                          {filteredItems &&
                            filteredItems.length > 0 &&
                            _.sortBy(
                              filteredItems.filter((i) =>
                                itemMatchesPurchasingCategory(i, c, pc.rootId)
                              ),
                              ["item_id"]
                            )?.map((i) => {
                              return (
                                <Item
                                  itemId={i.item_id}
                                  itemName={i.item_name}
                                  imageUri={i.imageUri}
                                  model={i.model}
                                  description={i.long_description}
                                  inStock={i.in_stock}
                                  maximum={i.maximum}
                                  minimum={i.minimum}
                                  outstanding={i.outstanding}
                                  addToCart={addToCart}
                                  isInCart={currentCartIds.filter((id) => id === i.item_id).length > 0}
                                />
                              );
                            })}
                        </Category>
                      ))}
                  </ParentCategory>
                );
              })}
              </>
            )}
          </div>

          <div
            onClick={() => {
              window.scrollTo({
                top: 0,
                behavior: "smooth",
              });
            }}
            className="to-top-btn"
          >
            <BiArrowToTop size={36} color="#ff4f00" />
          </div>
        </div>
    </>
  );
};

const Cart = (props) => {
  const { t } = useTranslation();
  const tBase = "views.purchasing.order.index";
  const tr = (key) => t(`${tBase}.${key}`);

  const [cart, setCart] = useState(null);

  const fetchCart = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/getCurrentCart`
    );

    // somewhat redundant since this rounding operation is also performed in the purchasingItems query
    function roundToTwoDecimals(value) {
      return Math.round(value * 100) / 100;
    }

    response.data.forEach((item) => {
      item.stock = roundToTwoDecimals(item.stock);
      item.minimum = roundToTwoDecimals(item.minimum);
      item.maximum = roundToTwoDecimals(item.maximum);
      item.outstanding = roundToTwoDecimals(item.outstanding);
    });

    setCart(response.data);
  };

  const submitCart = async () => {
    await axios.post(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/submitCart`,
      {
        cart,
      }
    );

    props.handleClose();
    props.fetchCounts();
    // #28 — only after a successful submit (a failed request throws above and
    // leaves the user on the cart).
    if (props.onCartSubmitted) {
      props.onCartSubmitted();
    }
  };

  useEffect(() => {
    fetchCart();
  }, []);

  return (
    <div className="cart-modal">
      {cart ? (
        <>
          <div className="cart-modal-top-row">
            <div>{tr("Order")} {cart.length > 0 ? cart[0].id : null}</div>

            <AiOutlineCloseCircle
              className="modal-close-btn"
              color="red"
              size={28}
              onClick={props.handleClose}
            />
          </div>
          <div className="cart-modal-body">
            {cart.length > 0 &&
              cart?.map((item, index) => {
                return (
                  <div key={item.cart_item_id || index} className="cart-row">
                    <div className="left-group">
                      <div>
                        <div style={{ display: "flex", flexDirection: "row" }}>
                          <div>{item.pc_name}</div>
                          <div>{" -> "}</div>
                          <div>{item.category_name}</div>
                        </div>
                        <div>{item.item_name}</div>
                      </div>
                    </div>
                    <div className="middle-group">
                      <div className="group">
                        <div className="center">{tr("Minimum")}:</div>
                        <div className="center">{item.minimum}</div>
                      </div>
                      <div className="group">
                        <div className="center">{tr("Maximum")}:</div>
                        <div className="center">{item.maximum}</div>
                      </div>
                      <div className="group">
                        <div className="center">{tr("In Stock")}:</div>
                        <div className="center">{item.stock}</div>
                      </div>
                      <div className="group">
                        <div className="center">{tr("Recommended")}:</div>
                        <div className="center">
                          {item.maximum - (item.stock + item.outstanding)}
                        </div>
                      </div>

                      <div
                        className="group"
                        style={{
                          display: "flex",
                          flexDirection: "row",
                        }}
                      >
                        <Input
                          className="qty-input"
                          item={item}
                          fetchCart={fetchCart}
                        />
                        <BsTrash
                          color="red"
                          size={16}
                          className="trash"
                          style={{ marginLeft: 20, marginTop: 5, cursor: "pointer" }}
                          onClick={async () => {
                            // Capture original cart for error recovery
                            const originalCart = cart;
                            
                            // Optimistic UI update - remove item immediately
                            const updatedCart = cart.filter(cartItem => cartItem.cart_item_id !== item.cart_item_id);
                            setCart(updatedCart);
                            
                            try {
                              // Call API to remove from server
                              const updatedCartFromServer = await props.removeCartItem(item.cart_item_id);
                              
                              // If server returns updated cart, use it (with proper rounding)
                              if (updatedCartFromServer) {
                                function roundToTwoDecimals(value) {
                                  return Math.round(value * 100) / 100;
                                }
                                
                                updatedCartFromServer.forEach((cartItem) => {
                                  cartItem.stock = roundToTwoDecimals(cartItem.stock);
                                  cartItem.minimum = roundToTwoDecimals(cartItem.minimum);
                                  cartItem.maximum = roundToTwoDecimals(cartItem.maximum);
                                  cartItem.outstanding = roundToTwoDecimals(cartItem.outstanding);
                                });
                                
                                setCart(updatedCartFromServer);
                              }
                            } catch (error) {
                              // If API call fails, revert to previous cart state
                              setCart(originalCart);
                              console.error('Error removing item from cart:', error);
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
          <div className="cart-modal-bottom-row">
            <Button primary onClick={submitCart}>
              {tr("Submit")}
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
};

const Input = (props) => {
  const [quantity, setQuantity] = useState(props.item.quantity);

  const updateQuantity = async (cartItemId, itemId, quantity) => {
    if (quantity && quantity >= 0) {
      const response = await axios.put(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/updateQuantity`,
        {
          cartItemId,
          itemId,
          quantity,
        }
      );

      if (response.status === 200) {
        setQuantity(quantity);
        props.fetchCart();
      }
    }
  };

  return (
    <>
      <input
        className="purchasing-quantity"
        value={quantity}
        onBlur={(e) => {
          updateQuantity(
            props.item.cart_item_id,
            props.item.item_id,
            e.target.value
          );
        }}
      />
    </>
  );
};

const PurchasingInput = (props) => {
  const [quantity, setQuantity] = useState(
    props.max - props.quantity - props.outstanding < 0
      ? 0
      : props.max - props.quantity - props.outstanding
  );

  const [inCart, setInCart] = useState(false);

  const handleChange = (newQuantity) => {
    if (parseInt(newQuantity) && parseInt(newQuantity) >= 0) {
      setQuantity(newQuantity);
    } else {
      setQuantity(0);
    }
  };

  useEffect(() => {
    if (props.currentCartIds.filter((id) => id == props.itemId).length > 0) {
      setInCart(true);
    }
  }, []);

  return (
    <>
      <input
        className="purchasing-quantity"
        value={quantity}
        onChange={(e) => handleChange(e.target.value)}
      />
      <IconButton disabled={inCart} primary={true} size="large">
        <FiPlus
          onClick={() => {
            props.addToCart(props.itemId, quantity);
            setInCart(true);
          }}
        />
      </IconButton>
    </>
  );
};

export default Order;
